// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import Notify from "@bg/App/Components/Notify";
import loadSystemList from "@bg/App/Handlers/CommonHandlers/LoadSystemList";
import { CustomAppState } from "@bg/App/Store/Reducers";
import { fontConfig } from "@bg/XDL/PreStyle/typography";
import { styles } from "@bgs/App/Widgets/ProjectPlanner/ProjectPlannerStyles";
import LazyImage, { LazyImageProps } from "@bgs/MobiusOverrides/LazyImage/LazyImage";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import loadWishLists from "@insite/client-framework/Store/Data/WishLists/Handlers/LoadWishLists";
import updateWishList from "@insite/client-framework/Store/Data/WishLists/Handlers/UpdateWishList";
import { getWishListsDataView } from "@insite/client-framework/Store/Data/WishLists/WishListsSelectors";
import { getPageLinkByPageType, useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import addWishList from "@insite/client-framework/Store/Pages/MyLists/Handlers/AddWishList";
import translate from "@insite/client-framework/Translate";
import { WishListModel } from "@insite/client-framework/Types/ApiModels";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonIcon } from "@insite/mobius/Button";
import Clickable from "@insite/mobius/Clickable";
import Icon from "@insite/mobius/Icon";
import Link from "@insite/mobius/Link";
import LoadingOverlay from "@insite/mobius/LoadingOverlay";
import LoadingSpinner from "@insite/mobius/LoadingSpinner";
import TextField from "@insite/mobius/TextField";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography from "@insite/mobius/Typography";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import React, { useContext, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: CustomAppState) => {
    const groupListName = state.context.website.properties["groupsListName"];
    return {
        listGroups: (state.xSystemList as any)[groupListName]?.values,
        uncategorizedGroupName: (getSettingsCollection(state).customSettings as any).unCategorizedGroupName as string,
        wishListsDataView: getWishListsDataView(state, { page: 1, pageSize: 100 }),
        isAuth: state.context.session.isAuthenticated,
        listDetailsLink: getPageLinkByPageType(state, "MyListsDetailsPage")?.url,
        groupListName,
        groupsNamingRegex: (getSettingsCollection(state).customSettings as any).groupsNamingRegex as string,
    };
};

const mapDispatchToProps = {
    loadWishLists,
    addWishList,
    loadSystemList,
    updateWishList,
};

export interface GroupList {
    additionalInfo: string;
    description: string;
    id: string;
    name: string;
}

const enum fields {
    title = "title",
    url = "url",
    description = "description",
    backgroundImage = "backgroundImage",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.url]: LinkFieldValue;
        [fields.description]: string;
        [fields.backgroundImage]: string;
    };
}

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

const ProjectPLanner: React.FC<Props> = ({
    fields: { title, url, description, backgroundImage },
    listGroups,
    uncategorizedGroupName,
    wishListsDataView,
    loadWishLists,
    addWishList,
    loadSystemList,
    isAuth,
    updateWishList,
    listDetailsLink,
    history,
    groupListName,
    groupsNamingRegex,
}) => {
    if (!isAuth) {
        return null;
    }
    const [selectedGroup, setSelectedGroup] = useState("");
    const [currentStep, setCurrentStep] = useState("");
    const [groupName, setGroupName] = useState("");
    const [newGroupError, setNewGroupError] = useState("");
    const [inProgress, setInProgress] = useState(false);
    const [newListName, setNewListName] = useState("");
    const [newListNameError, setNewListNameError] = useState<React.ReactNode>("");
    const [listAlreadyExists, setListAlreadyExists] = useState(false);
    const [groupAlreadyExistsError, setGroupAlreadyExistsError] = useState("");

    const toasterContext = useContext(ToasterContext);

    useEffect(() => {
        if (!listGroups || !listGroups.length) {
            loadSystemList({
                systemListName: groupListName,
            });
        }
    }, []);

    useEffect(() => {
        if (!wishListsDataView.value && !wishListsDataView.isLoading && currentStep === "") {
            setTimeout(() => {
                loadWishLists({
                    page: 1,
                    sort: "ModifiedOn DESC",
                    expand: ["top3products"],
                    pageSize: 100,
                });
            }, 100);
        }
    }, [currentStep]);

    useEffect(() => {
        setNewListNameError("");
        setNewGroupError("");
        setGroupAlreadyExistsError("");
    }, [currentStep]);

    const newListInputChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        setNewListName(event.target.value);
        if (event.target.value) {
            if (wishListsDataView && wishListsDataView.value) {
                const projectListExists =
                    wishListsDataView.value.filter(
                        list => list.name.toLowerCase() === event.target.value.trim().toLowerCase(),
                    )?.length > 0;

                setListAlreadyExists(projectListExists);
            }
            setNewListNameError("");
        }
    };

    const newGroupInputChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        setGroupName(event.target.value);
        if (event.target.value) {
            setNewGroupError("");
        }
    };

    const addNewGroupAndGoBack = () => {
        if (!groupName || !groupName.length) {
            setNewGroupError("Group Name Requried");
            return;
        }
        setSelectedGroup(groupName);
        setCurrentStep("last-step");
    };

    const handleGroupSelection = (groupName: string) => {
        if (selectedGroup.toLowerCase() === groupName.toLowerCase()) {
            setSelectedGroup("");
        } else {
            setSelectedGroup(groupName);
        }
    };

    const submitWishListUpdate = () => {
        if (!newListName) {
            setNewListNameError("Project name is required");
            return;
        }
        if (newListName && listAlreadyExists && !selectedGroup) {
            setNewListNameError("Nothing to update, select a group");
            return;
        }

        let groups: string[] = [];
        let wishList = {} as WishListModel;
        // if the project list already exists in the users list and the selected group already exists as well, dont update
        if (selectedGroup && listAlreadyExists && wishListsDataView && wishListsDataView.value) {
            const existingProject = wishListsDataView.value.filter(
                list => list.name.toLowerCase() === newListName.trim().toLowerCase(),
            )?.[0];
            if (existingProject && existingProject.properties.groups.length) {
                wishList = existingProject;
                groups = JSON.parse(existingProject.properties.groups);
                if (groups.filter((x: string) => x.toLowerCase() === selectedGroup.toLowerCase()).length) {
                    setGroupAlreadyExistsError("Group already exists in the selected project");
                    return;
                }
            }
        }

        if (listAlreadyExists) {
            groups.push(selectedGroup);
            setInProgress(true);
            updateWishList({
                apiParameter: {
                    wishList: {
                        ...wishList,
                        properties: {
                            ...wishList.properties,
                            groups: groups.join(),
                        },
                    },
                },
                onSuccess: onSubmitSuccess as any,
                onError: onSubmitError,
                onComplete(resultProps) {
                    setInProgress(false);
                    if (resultProps.result?.wishList) {
                        // eslint-disable-next-line react/no-this-in-sfc
                        (this.onSuccess as any)?.(resultProps.result.wishList);
                    } else if (resultProps.result?.errorMessage) {
                        // eslint-disable-next-line react/no-this-in-sfc
                        this.onError?.(resultProps.result.errorMessage);
                    }
                },
            });
        } else {
            groups.push(uncategorizedGroupName.toLowerCase());
            selectedGroup ? groups.push(selectedGroup.toLowerCase()) : null;
            setInProgress(true);
            addWishList({
                apiParameter: {
                    name: newListName,
                    groups,
                } as any,
                onSuccess: onSubmitSuccess,
                onError: onSubmitError,
                onComplete(resultProps) {
                    setInProgress(false);
                    if (resultProps.result?.wishListModel) {
                        // eslint-disable-next-line react/no-this-in-sfc
                        this.onSuccess?.(resultProps.result?.wishListModel);
                    } else if (resultProps.result?.errorMessage) {
                        // eslint-disable-next-line react/no-this-in-sfc
                        this.onError?.(resultProps.result.errorMessage);
                    }
                },
            });
        }
    };

    const onSubmitSuccess = (wishList: WishListModel) => {
        toasterContext.addToast({
            body: translate(`Project List ${listAlreadyExists ? "Updated" : "Created"}`),
            messageType: "success",
        });
        resetForm();
        if (wishList) {
            const listUrl = `${listDetailsLink}?id=${wishList.id}`;
            history.push(listUrl);
        }
    };

    const resetForm = () => {
        setSelectedGroup("");
        setCurrentStep("");
        setGroupName("");
        setNewGroupError("");
        setInProgress(false);
        setNewListName("");
        setNewListNameError("");
        setListAlreadyExists(false);
        setGroupAlreadyExistsError("");
    };

    const onSubmitError = (errorMessage: string) => {
        setInProgress(false);
        setNewListNameError(errorMessage);
    };

    return (
        <LoadingOverlay loading={inProgress}>
            <StyledWrapper {...styles.widgetWrapper} {...({ backgroundImage } as any)}>
                <StyledWrapper {...styles.titleSection}>
                    <StyledWrapper
                        css={css`
                            display: flex;
                            align-items: baseline;
                            justify-content: center;
                        `}
                    >
                        {title && <Typography {...styles.title}>{title}</Typography>}
                        {url && (
                            <Link href={useGetLink(url)?.url} {...styles.urlStyling}>
                                <Icon src="ChevronRight" color="secondary" size={23} />
                            </Link>
                        )}
                    </StyledWrapper>
                    {description && <Typography size={fontConfig.fontSize.small}>{description}</Typography>}
                </StyledWrapper>
                <StyledWrapper {...styles.container} data-test-selector="projectPlanner">
                    <StyledWrapper {...styles.stepWrapper} className={currentStep === "" ? "in-view" : ""}>
                        <Typography {...styles.stepTitle}>
                            {siteMessage("Custom_ProjectPlannerWhichRoomMessage")}
                        </Typography>
                        {!listGroups && (
                            <StyledWrapper
                                css={css`
                                    height: 200px;
                                    width: 100%;
                                    display: flex;
                                    align-items: center;
                                    justify-content: center;
                                `}
                            >
                                <LoadingSpinner />
                            </StyledWrapper>
                        )}
                        {listGroups && listGroups.length > 0 && (
                            <StyledWrapper {...styles.groupsWrapper}>
                                {listGroups.map((group: GroupList) => (
                                    <Clickable
                                        className={
                                            selectedGroup.toLowerCase() === group.name.toLowerCase() ? "selected" : ""
                                        }
                                        key={group.id}
                                        {...styles.groupItem}
                                        onClick={() => handleGroupSelection(group.name)}
                                    >
                                        <GroupImage imagePath={group.additionalInfo} groupName={group.description} />
                                    </Clickable>
                                ))}
                                <StyledWrapper {...styles.groupItem}>
                                    <StyledWrapper {...styles.addNewGroupItem}>
                                        <Button
                                            variant="secondary"
                                            onClick={() => setCurrentStep("newGroup")}
                                            {...styles.addNewButton}
                                        >
                                            <ButtonIcon src="Plus" size={16} />
                                        </Button>
                                    </StyledWrapper>
                                    <Typography {...styles.groupNameText}>{translate("Blank Group")}</Typography>
                                </StyledWrapper>
                            </StyledWrapper>
                        )}
                        <StyledWrapper className="modal-actions">
                            <Button
                                {...styles.primaryButton}
                                onClick={() => setCurrentStep("last-step")}
                                // disabled={name.length > 100 || description.length > 300}
                                data-test-selector="projectPlanner_Next"
                            >
                                {translate("Next")}
                            </Button>
                        </StyledWrapper>
                    </StyledWrapper>

                    {/* new group name */}
                    <StyledWrapper {...styles.stepWrapper} className={currentStep === "newGroup" ? "in-view" : ""}>
                        <Typography {...styles.stepTitle}>{translate("New Group Name")}</Typography>
                        <TextField
                            label={translate("New Group Name")}
                            value={groupName}
                            error={newGroupError}
                            maxLength={50}
                            onChange={newGroupInputChangeHandler}
                            placeholder={translate("Type Group Name")}
                            {...styles.newListInput}
                            data-test-selector="projectPlanner_newGroupName"
                        />
                        {groupsNamingRegex && (
                            <StyledWrapper {...styles.notifyStyle}>
                                <Notify messageType="info" body={siteMessage("Custom_GroupName_Limitations_Text")} />
                            </StyledWrapper>
                        )}
                        <StyledWrapper className="modal-actions">
                            <Button
                                {...styles.primaryButton}
                                onClick={addNewGroupAndGoBack}
                                data-test-selector="projectPlanner_saveGroupAndMoveBack"
                                disabled={inProgress}
                            >
                                {translate("Save and Continue")}
                            </Button>
                            <Button onClick={() => setCurrentStep("")} {...styles.secondaryButton}>
                                {translate("Go Back")}
                            </Button>
                        </StyledWrapper>
                    </StyledWrapper>

                    {/* project list name */}
                    <StyledWrapper {...styles.stepWrapper} className={currentStep === "last-step" ? "in-view" : ""}>
                        <Typography {...styles.stepTitle}>
                            {siteMessage("Custom_ProjectPlannerProjectNameMessage")}
                        </Typography>
                        <TextField
                            label={translate("Project Name")}
                            value={newListName}
                            error={newListNameError || groupAlreadyExistsError}
                            maxLength={50}
                            onChange={newListInputChangeHandler}
                            placeholder={translate("Type Here")}
                            {...styles.newListInput}
                            data-test-selector="projectPlanner_newProjectName"
                        />
                        <StyledWrapper className="modal-actions">
                            <Button
                                {...styles.primaryButton}
                                onClick={submitWishListUpdate}
                                disabled={inProgress}
                                data-test-selector="projectPlanner_addButton"
                            >
                                {listAlreadyExists ? translate("Update Existing Project") : translate("Create Project")}
                            </Button>
                            <Button onClick={() => setCurrentStep("")} {...styles.secondaryButton}>
                                {translate("Go Back")}
                            </Button>
                        </StyledWrapper>
                    </StyledWrapper>
                </StyledWrapper>
            </StyledWrapper>
        </LoadingOverlay>
    );
};

export type GroupProps = {
    imagePath: string;
    groupName: string;
};

export const GroupImage = ({ imagePath, groupName }: GroupProps) => {
    return (
        <StyledWrapper className="item-wrapper">
            <LazyImage
                src={imagePath}
                width="120px"
                height="120px"
                cloudinaryWidth="115"
                cloudinary={true}
                css={css`
                    margin-bottom: 10px;
                `}
            />
            <Typography {...styles.groupNameText}>{groupName}</Typography>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: withHistory(connect(mapStateToProps, mapDispatchToProps)(ProjectPLanner)),
    definition: {
        group: "XNGAGE" as any,
        fieldDefinitions: [
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "Project Planner",
                fieldType: "Translatable",
            },
            {
                name: fields.url,
                displayName: "Url",
                editorTemplate: "LinkField",
                defaultValue: { type: "Url", value: "" },
                fieldType: "General",
            },
            {
                name: fields.description,
                displayName: "Sub Heading Text",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
            },
            {
                name: fields.backgroundImage,
                displayName: "Background Image",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
